import { useEffect, useState } from 'react';
import {
  dnaLabService,
  type DnaLabItem,
  type CreateDnaLabRequest,
} from '../../services/dna-lab.service';
import './AdminDnaLabs.css';

const TESTING_METHODS = [
  { value: 1, label: 'STR' },
  { value: 2, label: 'SNP' },
];

const emptyForm: CreateDnaLabRequest = {
  organizationName: '',
  testingMethod: 1,
  regionId: null,
  address: '',
  phoneNumber: '',
  email: '',
};

export default function AdminDnaLabs() {
  const [labs, setLabs] = useState<DnaLabItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<CreateDnaLabRequest>({ ...emptyForm });
  const [submitLoading, setSubmitLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  const loadLabs = () => {
    setLoading(true);
    dnaLabService
      .getAll()
      .then((res) => {
        if (res.success && res.data) setLabs(res.data);
        else setError(res.message || 'Ошибка загрузки');
      })
      .catch(() => setError('Ошибка загрузки данных'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadLabs();
  }, []);

  const handleChange = (field: keyof CreateDnaLabRequest, value: string | number | null) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setMessage(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.organizationName.trim()) {
      setMessage({ type: 'err', text: 'Укажите наименование организации' });
      return;
    }
    if (!form.phoneNumber.trim()) {
      setMessage({ type: 'err', text: 'Укажите номер телефона' });
      return;
    }
    if (!form.email.trim()) {
      setMessage({ type: 'err', text: 'Укажите email' });
      return;
    }
    setSubmitLoading(true);
    setMessage(null);
    dnaLabService
      .create(form)
      .then((res) => {
        if (res.success) {
          setMessage({ type: 'ok', text: 'ДНК лаборатория создана' });
          setForm({ ...emptyForm });
          loadLabs();
        } else {
          setMessage({ type: 'err', text: res.message || 'Ошибка создания' });
        }
      })
      .catch(() => setMessage({ type: 'err', text: 'Ошибка создания' }))
      .finally(() => setSubmitLoading(false));
  };

  const handleDelete = (id: number) => {
    if (!confirm('Удалить лабораторию?')) return;
    dnaLabService
      .delete(id)
      .then((res) => {
        if (res.success) loadLabs();
      })
      .catch(() => {});
  };

  return (
    <div className="admin-dnalab">
      <div className="admin-dnalab-header">
        <div>
          <h1>ДНК лаборатории</h1>
          <p className="admin-dnalab-desc">Управление ДНК лабораториями</p>
        </div>
        <button className="admin-dnalab-add-btn" onClick={() => setShowForm((s) => !s)}>
          {showForm ? 'Скрыть форму' : 'Добавить лабораторию'}
        </button>
      </div>

      {showForm && (
        <form className="admin-dnalab-form" onSubmit={handleSubmit}>
          <div className="admin-dnalab-form-grid">
            <div className="admin-dnalab-row">
              <label>Наименование организации *</label>
              <input
                type="text"
                value={form.organizationName}
                onChange={(e) => handleChange('organizationName', e.target.value)}
                required
                maxLength={500}
              />
            </div>
            <div className="admin-dnalab-row">
              <label>Метод тестирования *</label>
              <select
                value={form.testingMethod}
                onChange={(e) => handleChange('testingMethod', Number(e.target.value))}
              >
                {TESTING_METHODS.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="admin-dnalab-row">
              <label>Телефон *</label>
              <input
                type="tel"
                value={form.phoneNumber}
                onChange={(e) => handleChange('phoneNumber', e.target.value)}
                required
                maxLength={20}
              />
            </div>
            <div className="admin-dnalab-row">
              <label>Email *</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => handleChange('email', e.target.value)}
                required
                maxLength={255}
              />
            </div>
            <div className="admin-dnalab-row admin-dnalab-row--full">
              <label>Адрес</label>
              <input
                type="text"
                value={form.address ?? ''}
                onChange={(e) => handleChange('address', e.target.value || null)}
                maxLength={500}
              />
            </div>
          </div>
          {message && (
            <p className={message.type === 'ok' ? 'admin-dnalab-msg-ok' : 'admin-dnalab-msg-err'}>
              {message.text}
            </p>
          )}
          <div className="admin-dnalab-actions">
            <button type="submit" disabled={submitLoading}>
              {submitLoading ? 'Создание...' : 'Создать лабораторию'}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <p className="admin-dnalab-loading">Загрузка...</p>
      ) : error ? (
        <p className="admin-dnalab-error">{error}</p>
      ) : (
        <div className="admin-dnalab-table-wrap">
          <table className="admin-dnalab-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Организация</th>
                <th>Метод</th>
                <th>Регион</th>
                <th>Адрес</th>
                <th>Телефон</th>
                <th>Email</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {labs.map((lab) => (
                <tr key={lab.id}>
                  <td>{lab.id}</td>
                  <td>{lab.organizationName}</td>
                  <td>{lab.testingMethodName || (lab.testingMethod === 1 ? 'STR' : 'SNP')}</td>
                  <td>{lab.region?.nameRu ?? '—'}</td>
                  <td>{lab.address ?? '—'}</td>
                  <td>{lab.phoneNumber}</td>
                  <td>{lab.email}</td>
                  <td>
                    <button
                      className="admin-dnalab-del-btn"
                      onClick={() => handleDelete(lab.id)}
                    >
                      Удалить
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {labs.length === 0 && <p className="admin-dnalab-empty">Нет лабораторий</p>}
        </div>
      )}
    </div>
  );
}
